import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, User, Phone, MapPin, ArrowRight, Check, RefreshCw, Users, MessageSquare, Shield, Award, Star, Clock, Package, Globe, CreditCard, CheckCircle } from 'lucide-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { supabase } from '../lib/supabase';

const CreateAccountPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signUp } = useSupabase();
  
  // Get accountType from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const accountTypeParam = queryParams.get('accountType');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    phone: '',
    password: '',
    confirmPassword: '',
    accountType: accountTypeParam === 'supplier' ? 'supplier' : 'buyer',
    agreeToTerms: false,
    subscribeNewsletter: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [captchaEnabled, setCaptchaEnabled] = useState(false);

  const countries = [
    { name: 'United States', code: '+1' },
    { name: 'United Kingdom', code: '+44' },
    { name: 'Canada', code: '+1' },
    { name: 'Australia', code: '+61' },
    { name: 'Germany', code: '+49' },
    { name: 'France', code: '+33' },
    { name: 'Japan', code: '+81' },
    { name: 'India', code: '+91' },
    { name: 'Singapore', code: '+65' },
    { name: 'UAE', code: '+971' },
    { name: 'Italy', code: '+39' },
    { name: 'Spain', code: '+34' },
    { name: 'Netherlands', code: '+31' },
    { name: 'Belgium', code: '+32' },
    { name: 'Switzerland', code: '+41' },
    { name: 'Austria', code: '+43' },
    { name: 'Sweden', code: '+46' },
    { name: 'Norway', code: '+47' },
    { name: 'Denmark', code: '+45' },
    { name: 'Finland', code: '+358' }
  ];

  // Generate random captcha
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(result);
    setCaptchaInput('');
    setCaptchaError('');
  };

  // Check if all required fields are completed
  const isFormComplete = 
    formData.firstName.trim() !== '' &&
    formData.lastName.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.phone.trim() !== '' &&
    formData.password.trim() !== '' &&
    formData.confirmPassword.trim() !== '' &&
    formData.agreeToTerms;

  // Check captcha settings from localStorage
  React.useEffect(() => {
    const adminSettings = localStorage.getItem('admin_settings');
    if (adminSettings) {
      try {
        const settings = JSON.parse(adminSettings);
        setCaptchaEnabled(settings.enableCaptcha && settings.captchaOnRegister);
      } catch (err) {
        console.error('Error parsing admin settings:', err);
        setCaptchaEnabled(false);
      }
    } else {
      setCaptchaEnabled(false);
    }
  }, []);

  // Initialize captcha when form becomes complete
  React.useEffect(() => {
    if (isFormComplete && !captchaCode && captchaEnabled) {
      generateCaptcha();
    }
  }, [isFormComplete, captchaCode, captchaEnabled]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    // Check if form is complete and show specific error messages
    if (!isFormComplete) {
      const missingFields = [];
      if (!formData.firstName.trim()) missingFields.push('First name');
      if (!formData.lastName.trim()) missingFields.push('Last name');
      if (!formData.email.trim()) missingFields.push('Email address');
      if (!formData.phone.trim()) missingFields.push('Phone number');
      if (!formData.password.trim()) missingFields.push('Password');
      if (!formData.confirmPassword.trim()) missingFields.push('Confirm password');
      if (!formData.agreeToTerms) missingFields.push('Agreement to Terms of Service');
      
      if (missingFields.length > 0) {
        setSignupError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
        return;
      }
    }
    
    if (formData.password !== formData.confirmPassword) {
      setSignupError('Passwords do not match!');
      return;
    }

    if (!formData.agreeToTerms) {
      setSignupError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    if (isFormComplete && captchaEnabled) {
      if (captchaInput !== captchaCode) {
        setCaptchaError('Captcha code is incorrect. Please try again.');
        generateCaptcha();
        return;
      }
    }

    setIsLoading(true);
    setSignupError(null);
    
    try {
      // Sign up with Supabase
      await signUp(
        formData.email, 
        formData.password, 
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          country: formData.country,
          accountType: formData.accountType
        }
      );
      
      // If supplier account, create supplier profile
      if (formData.accountType === 'sourcing_agent') {
        try {
          // Get the newly created user
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            // Generate slug from name
            const slug = `${formData.firstName}-${formData.lastName}`
              .toLowerCase()
              .replace(/[^\w\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .trim();
            
            // Create sourcing agent profile
            const { error: supplierError } = await supabase
              .from('suppliers')
              .insert({
                user_id: user.id,
                organization: `${formData.firstName} ${formData.lastName}`,
                representative: `${formData.firstName} ${formData.lastName}`,
                address: '',
                email: formData.email,
                phone: formData.phone,
                mobile: formData.phone,
                products: 'Sourcing Services',
                slug: `${slug}-${Date.now()}`,
                subdomain: `${slug}.madeinbd.com`,
                verified: false,
                year_established: null,
                rating: 0,
                total_products: 0,
                description: 'Professional sourcing agent',
                specialties: ['Sourcing Services', 'Supplier Verification', 'Quality Control'],
                location: formData.country || 'International',
                city: formData.country || 'International'
              });
            
            if (supplierError) {
              console.error('Error creating sourcing agent profile:', supplierError);
            }
          }
        } catch (supplierCreationError) {
          console.error('Error in sourcing agent profile creation:', supplierCreationError);
          // Don't fail the signup process if sourcing agent profile creation fails
        }
      }
      
      // Redirect to success page or login
      if (formData.accountType === 'sourcing_agent') {
        navigate('/signin', { 
          state: { 
            message: 'Sourcing Agent account created successfully! You can now sign in to access your dashboard.'
          } 
        });
      } else {
        navigate('/signin', { 
          state: { 
            message: 'Account created successfully! You can now sign in to your account.'
          } 
        });
      }
    } catch (error) {
      console.error('Error creating account:', error);
      setSignupError(error instanceof Error ? error.message : 'An error occurred during signup');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    // If changing to supplier account type, automatically set country to Bangladesh
    if (name === 'accountType' && value === 'sourcing_agent') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        country: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    setCaptchaError('');
  };

  // Get country code based on selected country
  const getCountryCode = () => {
    if (formData.accountType === 'sourcing_agent') return '';
    
    const selectedCountry = countries.find(c => c.name === formData.country);
    return selectedCountry ? selectedCountry.code : '';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <img 
            src="/madeinbd-logo.png" 
            alt="Made in BD" 
            className="h-12 w-auto object-contain"
          />
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/signin" className="font-medium text-madeinbd-600 hover:text-madeinbd-500">
            Sign in here
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* How It Works Section */}
          <div className="bg-white py-8 px-6 shadow sm:rounded-lg">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">How It Works for International Buyers</h3>
              <p className="text-gray-600">Start sourcing from Bangladesh in 3 simple steps</p>
            </div>
            
            <div className="space-y-8">
              {/* Step 1: Browse Suppliers */}
              <div className="flex items-start space-x-4">
                <div className="relative flex-shrink-0">
                  <div className="bg-gradient-to-br from-blue-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center shadow-lg">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                    1
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Browse Verified Suppliers</h4>
                  <p className="text-gray-600 mb-3">
                    Explore our network of 500+ verified Bangladeshi suppliers. Filter by product category, location, and certifications.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      <Shield className="h-3 w-3 mr-1" />
                      Verified
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      <Award className="h-3 w-3 mr-1" />
                      Certified
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      <Star className="h-3 w-3 mr-1" />
                      Rated
                    </span>
                  </div>
                </div>
              </div>

              {/* Step 2: Send Inquiry */}
              <div className="flex items-start space-x-4">
                <div className="relative flex-shrink-0">
                  <div className="bg-gradient-to-br from-green-500 to-emerald-600 w-16 h-16 rounded-full flex items-center justify-center shadow-lg">
                    <MessageSquare className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                    2
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Send Inquiry & Get Quotes</h4>
                  <p className="text-gray-600 mb-3">
                    Contact suppliers directly through our platform. Send detailed inquiries, request samples, and receive competitive quotes.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <Clock className="h-3 w-3 mr-1" />
                      Fast Response
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <Package className="h-3 w-3 mr-1" />
                      Free Samples
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <Globe className="h-3 w-3 mr-1" />
                      Global Shipping
                    </span>
                  </div>
                </div>
              </div>

              {/* Step 3: Secure Deal */}
              <div className="flex items-start space-x-4">
                <div className="relative flex-shrink-0">
                  <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-full flex items-center justify-center shadow-lg">
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                    3
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Secure Deal with Escrow</h4>
                  <p className="text-gray-600 mb-3">
                    Complete your purchase with confidence using our secure escrow service. Your payment is protected until you confirm receipt.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                      <CreditCard className="h-3 w-3 mr-1" />
                      Secure Payment
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                      <Eye className="h-3 w-3 mr-1" />
                      Quality Check
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Guaranteed
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Call to Action */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="bg-gradient-to-r from-blue-50 to-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Ready to Start Sourcing?</h4>
                <p className="text-sm text-gray-600">
                  Join thousands of international buyers who trust Made in BD for their sourcing needs.
                </p>
              </div>
            </div>
          </div>

          {/* Registration Form */}
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Error Message */}
              {signupError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {signupError}
                </div>
              )}

              {/* Account Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Account Type *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                    formData.accountType === 'buyer' 
                      ? 'border-madeinbd-600 bg-madeinbd-50' 
                      : 'border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="accountType"
                      value="buyer" 
                      checked={formData.accountType === 'buyer'} 
                      onChange={handleChange}
                      className="sr-only"
                      required
                    />
                    <div className="flex flex-col">
                      <span className="block text-sm font-medium text-gray-900">Buyer</span>
                      <span className="block text-sm text-gray-500">Purchase products</span>
                    </div>
                    {formData.accountType === 'buyer' && (
                      <Check className="h-5 w-5 text-madeinbd-600 absolute top-4 right-4" />
                    )}
                  </label>

                  <label className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                    formData.accountType === 'sourcing_agent' 
                      ? 'border-madeinbd-600 bg-madeinbd-50' 
                      : 'border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="accountType"
                      value="sourcing_agent"
                      checked={formData.accountType === 'sourcing_agent'}
                      onChange={handleChange}
                      className="sr-only"
                      required
                    />
                    <div className="flex flex-col">
                      <span className="block text-sm font-medium text-gray-900">Sourcing Agent</span>
                      <span className="block text-sm text-gray-500">Help buyers find suppliers</span>
                    </div>
                    {formData.accountType === 'sourcing_agent' && (
                      <Check className="h-5 w-5 text-madeinbd-600 absolute top-4 right-4" />
                    )}
                  </label>
                </div>
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First name *
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-madeinbd-500 focus:border-madeinbd-500 sm:text-sm"
                      placeholder="First name"
                    />
                    <User className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                  </div>
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last name *
                  </label>
                  <div className="mt-1">
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-madeinbd-500 focus:border-madeinbd-500 sm:text-sm"
                      placeholder="Last name"
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-madeinbd-500 focus:border-madeinbd-500 sm:text-sm"
                    placeholder="Enter your email"
                  />
                  <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>

              {/* Country */}
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                  Country *
                </label>
                <div className="mt-1 relative">
                  <select
                    id="country"
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-madeinbd-500 focus:border-madeinbd-500 sm:text-sm"
                  >
                    <option value="">Select your country</option>
                    {countries.map((country) => (
                      <option key={country.name} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  <MapPin className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone number *
                </label>
                <div className="mt-1 relative flex">
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm rounded-l-md">
                      {getCountryCode() || 'Code'}
                    </span>
                  </div>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-r-md placeholder-gray-400 focus:outline-none focus:ring-madeinbd-500 focus:border-madeinbd-500 sm:text-sm"
                    placeholder="Phone number"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-madeinbd-500 focus:border-madeinbd-500 sm:text-sm"
                    placeholder="Create a password"
                  />
                  <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm password *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-madeinbd-500 focus:border-madeinbd-500 sm:text-sm"
                    placeholder="Confirm your password"
                  />
                  <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Checkboxes */}
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    id="agreeToTerms"
                    name="agreeToTerms"
                    type="checkbox"
                    required
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                    className="h-4 w-4 text-madeinbd-600 focus:ring-madeinbd-500 border-gray-300 rounded"
                  />
                  <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-gray-900">
                    I agree to the{' '}
                    <a href="#" className="text-madeinbd-600 hover:text-madeinbd-500">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="#" className="text-madeinbd-600 hover:text-madeinbd-500">
                      Privacy Policy
                    </a> *
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    id="subscribeNewsletter"
                    name="subscribeNewsletter"
                    type="checkbox"
                    checked={formData.subscribeNewsletter}
                    onChange={handleChange}
                    className="h-4 w-4 text-madeinbd-600 focus:ring-madeinbd-500 border-gray-300 rounded"
                  />
                  <label htmlFor="subscribeNewsletter" className="ml-2 block text-sm text-gray-900">
                    Subscribe to our newsletter for updates and offers
                  </label>
                </div>
              </div>

              {/* Error Message */}
              {signupError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {signupError}
                </div>
              )}

              {/* Captcha - Only show when form is complete */}
              {isFormComplete && captchaEnabled && (
                <div className="animate-fade-in">
                  <label htmlFor="captcha" className="block text-sm font-medium text-gray-700">
                    Security Verification *
                  </label>
                  <div className="mt-1 space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="bg-gray-100 px-4 py-2 rounded-md border border-gray-300 font-mono text-lg tracking-wider select-none">
                        {captchaCode}
                      </div>
                      <button
                        type="button"
                        onClick={generateCaptcha}
                        className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                        title="Refresh captcha"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    </div>
                    <input
                      id="captcha"
                      name="captcha"
                      type="text"
                      required
                      value={captchaInput}
                      onChange={(e) => {
                        setCaptchaInput(e.target.value);
                        setCaptchaError('');
                      }}
                      className={`appearance-none block w-full px-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-madeinbd-500 focus:border-madeinbd-500 sm:text-sm ${
                        captchaError ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter the code above"
                    />
                    {captchaError && (
                      <p className="text-sm text-red-600">{captchaError}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading || (isFormComplete && captchaEnabled && captchaInput !== captchaCode)}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-madeinbd-gradient hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-madeinbd-500 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating account...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      Create Account
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  )}
                </button>
              </div>
            </form>

            {/* Supplier Benefits */}
            {formData.accountType === 'sourcing_agent' && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Sourcing Agent Benefits</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Connect buyers with suppliers</li>
                  <li>• Earn commission on successful deals</li>
                  <li>• Access to verified supplier network</li>
                  <li>• 24/7 customer support</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAccountPage;